import React, { useEffect, useRef, useState } from 'react';
import { useProjectToday } from '../lib/projectToday';

/* A test job's test date, beside the Gantt's Today button (Duncan 2026-10-20). Everything on the job runs
   as of it; "Use real date" clears it. Shows only on a test job.
   Typing goes into the box as typed and is saved once it's a real date (a year 2000-2099; while the
   year is half typed the box already reads a valid "0002-03-15"), a moment after the last keystroke,
   or on Enter / leaving the box. Saving on every change -- and disabling the box meanwhile -- took one
   character at a time (Duncan, the same day). */
const PLAUSIBLE = /^20\d{2}-\d{2}-\d{2}$/;
const SAVE_AFTER_MS = 700;

const TestDateControl = () => {
  const { testDate, isTest, setTestDate } = useProjectToday();
  const [draft, setDraft] = useState(testDate || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const timer = useRef(null);
  const editing = useRef(false);

  // The saved date wins whenever the box isn't being typed in.
  useEffect(() => { if (!editing.current) setDraft(testDate || ''); }, [testDate]);
  useEffect(() => () => clearTimeout(timer.current), []);

  if (!isTest || !setTestDate) return null;

  const save = async (value) => {
    clearTimeout(timer.current);
    if ((value || null) === (testDate || null)) return;
    setBusy(true);
    setError(null);
    try {
      await setTestDate(value || null);
    } catch (e) {
      setError(e.message || 'Could not save the test date');
    } finally {
      setBusy(false);
    }
  };
  const saveIfReal = (value) => { if (PLAUSIBLE.test(value)) save(value); };

  return (
    <span
      title="This test job runs as of this date: the today line, task progress limits, billing dates and reports."
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, padding: '3px 6px 3px 10px',
        border: '1.5px solid var(--warning)', borderRadius: 7, background: 'var(--warning-soft)',
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--warning)', whiteSpace: 'nowrap' }}>Test date</span>
      <input
        type="date"
        aria-label="Test date"
        value={draft}
        onFocus={() => { editing.current = true; }}
        onChange={(e) => {
          const v = e.target.value;
          setDraft(v);
          clearTimeout(timer.current);
          if (PLAUSIBLE.test(v)) timer.current = setTimeout(() => save(v), SAVE_AFTER_MS);
        }}
        onKeyDown={(e) => { if (e.key === 'Enter') saveIfReal(draft); }}
        onBlur={() => { editing.current = false; if (PLAUSIBLE.test(draft)) save(draft); else setDraft(testDate || ''); }}
        style={{
          height: 26, padding: '0 6px', fontSize: 13, fontFamily: 'var(--font-mono)', color: 'var(--text-1)',
          background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 6,
        }}
      />
      {testDate && (
        <button
          type="button"
          disabled={busy}
          onClick={() => { setDraft(''); save(null); }}
          style={{ background: 'none', border: 'none', padding: '0 4px', fontSize: 12.5, color: 'var(--text-2)', cursor: 'pointer', whiteSpace: 'nowrap' }}
        >Use real date</button>
      )}
      {error && <span role="alert" style={{ fontSize: 12, color: 'var(--danger)' }}>{error}</span>}
    </span>
  );
};

export default TestDateControl;
